import { useState, useEffect, useCallback, useMemo } from 'react'
import { Box, Stack, Typography, Chip, Button, Tabs, Tab } from '@mui/material'
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart'
import SmartDisplayOutlinedIcon from '@mui/icons-material/SmartDisplayOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import { useIsMobile } from '@/hooks/Responsive'
import AlertConfirmModal from '@components/Modal/AlertConfirmModal'
import IntroductionTab from './IntroductionTab'
import LectureListTab from './LectureListTab'
import InquiryModal from '@components/Modal/InquiryModal'
import { getVLabDetail, insertVLabLectureReserve } from '@/api/vx'
import { useAuth } from '@/components/AuthContext'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@/data/nav'
import { useConfirm } from '@/components/ConfirmProvider'

export function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}

function formatEduTime(tm) {
  let seconds = Number(tm);
  if (seconds < 60) {
    	return '00:' + seconds
	}
	// sec
	var mins = Math.floor(seconds / 60);
	var secs = seconds - mins*60
	return addZero(mins) + ':' + addZero(secs)
    
	function addZero(num) {
		return ((num < 10) ? '0' : '') + num
	}
}

export default function VLabLectureView() {
  const navigate = useNavigate()
  const location = useLocation()
  const lang = useLang()
  const { id } = useParams()
  const { user } = useAuth();
  const { isMobile } = useIsMobile()
  const [searchParams, setSearchParams] = useSearchParams()

  const [selected, setSelected] = useState(null)
  const [alertOpen, setAlertOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [inquiryOpen, setInquiryOpen] = useState(false)
  const [lectureOpen, setLectureOpen] = useState(false)
  const [reserved, setReserved] = useState(false)
  const [alertDesc, setAlertDesc] = useState('');

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [instYn, setInstYn] = useState('N')
  const { alert, confirm } = useConfirm();

  // 데모 데이터
  const imgArr = useMemo(() => ([
    { id: 1, thumbUrl: 'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/A004/A004_1.png' },
    { id: 2, thumbUrl: 'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/A003/A003_1.png' },
    { id: 3, thumbUrl: 'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/A005/A005_1.png' },
    { id: 4, thumbUrl: 'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/B003/B003_1.png' },
    { id: 5, thumbUrl: 'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/C001/C001_1.png' },
    { id: 6, thumbUrl: 'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/C002/C002_1.png' },
  ]), [])

  const handleLectureClick = useCallback((row) => {
    setSelected(row)
    setLectureOpen(true)
  }, [])

  const handleConfirmAccept = () => {
    setConfirmOpen(false)

    navigate(buildLangHref('/login', lang), {
      state: {
        redirectTo: location.pathname + location.search,
      },
    })
  }

  const insertLectureReserve = async () => {
    try {
      let params = {
        accountId: user?.accountId,
        eduLectureId: id,
        userName: user?.name,
        instYn: instYn
      };
      const response = await insertVLabLectureReserve(params);
      if(response?.data.traineeYn === "Y") {
        alert('해당 강좌는 이미 수강신청된 강좌입니다.\n마이페이지를 통해 확인해 주세요.')
        return;
      }

      if(response?.data.receiptChkYn === "Y") {
        alert('해당 강좌의 수강신청기간이 아닙니다.');
        return;
      }
      if(response?.data?.traineeReserveResult === "success" && response?.data?.traineeResult === "success" && response?.data?.traineeRegResult === "success") {
        setReserved(true);
        setAlertOpen(true);
        setAlertDesc(instYn === 'Y' ? '담당자 승인 후 ':'');
      }
    } catch (error) {
      console.error(error);
    }
    
  }
  


  //수강신청 눌렀을때
  const handleApply = () => {
    if(user){
      insertLectureReserve();
    }else{
      setConfirmOpen(true)
    }
  }

  const tabKeys = ['intro', 'list']
  const initialTab =
    tabKeys.indexOf(searchParams.get('tab')) >= 0
      ? tabKeys.indexOf(searchParams.get('tab'))
      : 0

  const [value, setValue] = useState(initialTab)

  const handleTabChange = (e, newValue) => {
    const newTabKey = tabKeys[newValue]

    const currentParams = Object.fromEntries(searchParams)
    const nextParams = { ...currentParams, tab: newTabKey }
    const qs = new URLSearchParams(nextParams).toString()

    window.history.pushState({}, '', `${window.location.pathname}?${qs}`)
    setSearchParams(nextParams)

    setValue(newValue)
  }

  // URL 변할 때 상태 반영
  useEffect(() => {
    const t = searchParams.get('tab')
    const idx = tabKeys.indexOf(t)
    if (idx >= 0) setValue(idx)
  }, [searchParams])

  // ★★★ 상세 API 연동 ★★★
  useEffect(() => {
    if (!id) return

    let mounted = true

    const fetchDetail = async () => {
      try {
        setLoading(true)
        setError(null)

        // getVLabDetail 시그니처에 맞게 조정해서 사용
        const res = await getVLabDetail({ eduLectureId: id, accountId: user?.accountId })
        const raw = res?.data

        if (!raw) {
          throw new Error('NO_DATA')
        }

        // if(raw?.traineeYn === 'Y') setReserved(true)

        const mapped = {
          id: raw.eduLectureId ?? id, //강좌ID
          title: raw.eduTitle ?? '',  //제목
          categoryPath: [ //카테고리
            raw.eduGrandDivName,
            raw.eduMidiumDivName,
            raw.eduSmallDivName,
          ].filter(Boolean),
          applyPeriod:  //수강신청기간
            raw?.receiptBeginDt && raw?.receiptEndDt ? `${raw?.receiptBeginDt} ~ ${raw?.receiptEndDt}` : '',
          lecturePeriod:  //강좌학습기간
            raw?.eduBeginDt && raw?.eduEndDt
              ? `${raw?.eduBeginDt} ~ ${raw?.eduEndDt}`
              : '',
          author: 'CC BY',  //저작권
          // hashtags: ['#해시태그1', '#해시태그2', '#해시태그3'], //해시태그
          thumbUrl: raw.thumbnailImgUrl ? raw.thumbnailImgUrl : null,//|| imgArr[Math.floor(Math.random() * imgArr.length)].thumbUrl, //썸네일
          level: raw?.ldfcyCdNm ?? '초급',  //레벨
          totalSession: raw.eduCourseCnt || '', //강좌수
          // runtime: '30',  //강좌시간
          runtime: raw?.eduTime,
          lectureUrl: raw.lectureUrl || '', //강좌URL
          institution: raw.instYn === 'Y' ? true : false,  //기관여부
          // 소개 : IntroductionTab.jsx
          // 강의목록 : LectureListTab.jsx
        }

        setInstYn(raw?.instYn ?? 'N'); 

        if (mounted) {
          setData(mapped)
        }
      } catch (err) {
        if (mounted) {
          setError(err)
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    fetchDetail()

    return () => {
      mounted = false
    }
  }, [id])

  // 로딩 상태
  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          py: 10,
          textAlign: 'center',
          color: 'text.secondary',
          typography: 'body2',
        }}
      >
        <Typography>강좌 정보를 불러오는 중입니다...</Typography>
      </Box>
    )
  }

  // 에러 또는 데이터 없음
  if (!data || error) {
    return (
      <Box
        sx={{
          width: '100%',
          py: 10,
          textAlign: 'center',
          color: 'text.secondary',
          typography: 'body2',
        }}
      >
        <Stack alignItems="center" spacing={1.5}>
          <ErrorOutlineIcon sx={{ fontSize: '32px', color: 'grey.light' }} />
          <Typography color="grey.500">
            강좌 정보를 찾을 수 없습니다.
          </Typography>
        </Stack>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ mb: 6 }}>
        {/* 제목 */}
        <Box sx={{ display: isMobile ? 'block' : 'inline-flex', gap: 1, mb: 3 }}>
          {/* 기관 */}
          {data.institution && (
            <Chip
              label="기관"
              color="grey"
              variant="contained"
              component="span"
              sx={{
                typography: 'body1',
                fontWeight: '600',
                mt: 0.5,
                zIndex: 2,
              }}
            />
          )}
           <Typography 
            component="span" 
            variant={isMobile ? 'subtitle2' : 'subtitle1'} 
            fontWeight={700} 
            sx={{ 
              display: isMobile ? 'block' : 'inline-flex', 
              mt: isMobile ? 1 : 0 
            }}
          >
            {data.title}
          </Typography>
        </Box>

        <Stack direction={isMobile ? 'column' : 'row'} spacing={5}>
          {/* 썸네일 */}
          <Box
            sx={{
              position: 'relative',
              width: isMobile ? '100%' : '592px',
              aspectRatio: '16/9',
              borderRadius: 2,
              overflow: 'hidden',
              mb: 3,
              flex: 1,
            }}
          >
            {data.thumbUrl && data.thumbUrl.trim() !== '' ? (
              <img
                src={data.thumbUrl}
                alt={data.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <img
                src="/assets/images/No image.png"
                alt="이미지 없음"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}

            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{
                position: 'absolute',
                bottom: '0',
                left: '0',
                bgcolor: (theme) => `${theme.palette.grey[900]}BF`,
                width: '100%',
                height: '48px',
                p: 2,
                zIndex: 2,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <StackedBarChartIcon
                  fontSize="small"
                  sx={{ color: 'text.contrastText' }}
                />
                <Typography color="text.contrastText">
                  {data.level}
                </Typography>
              </Stack>
              <Box
                sx={{
                  width: '1px',
                  height: '12px',
                  bgcolor: 'divider',
                }}
              />
              <Stack direction="row" alignItems="center" spacing={1}>
                <SmartDisplayOutlinedIcon
                  fontSize="small"
                  sx={{ color: 'text.contrastText' }}
                />
                <Typography color="text.contrastText">
                  총 {data.totalSession}강
                </Typography>
              </Stack>
              <Box
                sx={{
                  width: '1px',
                  height: '12px',
                  bgcolor: 'divider',
                }}
              />
              <Stack direction="row" alignItems="center" spacing={1}>
                <AccessTimeOutlinedIcon
                  fontSize="small"
                  sx={{ color: 'text.contrastText' }}
                />
                <Typography color="text.contrastText">
                  {/* {data?.runtime ? Number(data?.runtime) / 60 : 0}분 */}
                  {data?.runtime ? formatEduTime(data?.runtime): '00:00'}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/* 정보 */}
          <Stack sx={{ flex: 1 }}>
            {/* 카테고리 경로 */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              {data.categoryPath.join(' > ')}
            </Typography>

            {/* 기간 정보 */}
            <Stack sx={{ mb: 5 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Typography fontWeight={500}>수강신청 기간</Typography>
                <Typography>{data.applyPeriod}</Typography>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Typography fontWeight={500}>강좌학습 기간</Typography>
                <Typography>{data.lecturePeriod}</Typography>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Typography fontWeight={500}>저작권</Typography>
                <Typography>{data.author}</Typography>
              </Stack>
            </Stack>

            {/* 해시태그 - 존재하지 않아 제외*/}
            {/* <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 3, flexWrap: 'wrap' }}
            >
              {data.hashtags.map((tag, i) => (
                <Chip key={i} label={tag} color="grey" variant="outlined" />
              ))}
            </Stack> */}

            {/* 버튼 영역 */}
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 'auto', '& > *': { flex: '1 1 50%' } }}
            >
              { user &&
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={() => setInquiryOpen(true)} // 로그인했을때
                  // onClick={() => setConfirmOpen(true)} // 로그인 상태가 아닐때
                >
                  문의하기
                </Button>
              }
              <Button
                variant="contained"
                color="primary"
                size="large"
                // disabled={reserved}
                sx={{ display: reserved ? 'none' : 'block' }}
                onClick={() => handleApply()}
              >
                수강신청
              </Button>

              <AlertConfirmModal
                open={confirmOpen}
                title="로그인이 필요한 서비스 입니다."
                description="로그인 페이지로 이동하겠습니까?"
                type="confirm"
                onConfirm={handleConfirmAccept}
                onClose={() => setConfirmOpen(false)}
                onCancel={() => setConfirmOpen(false)}
              />

              <AlertConfirmModal
                open={alertOpen}
                title="수강신청이 완료되었습니다."
                description={alertDesc+"마이페이지에서 확인 하실 수 있습니다."}
                type="alert"
                onConfirm={() => setAlertOpen(false)}
                onClose={() => setAlertOpen(false)}
              />

              <InquiryModal
                open={inquiryOpen}
                onClose={() => setInquiryOpen(false)}
                data={{
                  ...data,
                  eduLectureId: data?.eduLectureId, // 서버로 보낼 강좌ID 확정
                  title: data?.title,                           // 화면 표시용 강좌명
                }}
              />
              {/* 수강생에 한해 노출 */}
              {/* <Button
                variant="contained"
                color="primary"
                size="large"
                sx={{ flex: 1 }}
                onClick={() => handleLectureClick(data)}
              >
                강의보기
              </Button> 
              <BasicModal
                open={lectureOpen}
                onClose={() => setLectureOpen(false)}
                title={selected?.title}
                disableEnforceFocus
                disablePortal
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    paddingTop: '56.25%',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="iframe"
                    src={selected?.lectureUrl || null}
                    title="YouTube video player"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 0,
                    }}
                  />
                </Box>
              </BasicModal> */}
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* 탭 */}
      <Tabs
        value={value}
        onChange={handleTabChange}
        onClick={(e) => {
          const tabEls = e.currentTarget.querySelectorAll('.MuiTab-root')
          const clickedTab = e.target.closest('.MuiTab-root')
          if (!clickedTab) return

          const clickedIndex = Array.from(tabEls).indexOf(clickedTab)

          if (clickedIndex === value) {
            handleTabChange(e, value)
          }
        }}
        variant="fullWidth"
        aria-label="상세 탭"
        textColor="inherit"
        TabIndicatorProps={{
          sx: { height: '2px', bgcolor: 'grey.800' },
        }}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          mb: 2,
          '& .MuiTab-root': {
            color: 'grey.500',
            px: isMobile ? 2 : 4,
            flex: '1 1 0',
            typography: 'body1',
            fontWeight: '600',
            minWidth: 'initial',
            height: '48px',
            lineHeight: 1,
            textTransform: 'none',
            whiteSpace: 'nowrap',
          },
          '& .Mui-selected': {
            color: 'grey.800',
          },
        }}
      >
        <Tab label="소개" />
        <Tab label="강의목록" />
      </Tabs>

      {/* 탭 콘텐츠 */}
      <TabPanel value={value} index={0}>
        <IntroductionTab />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <LectureListTab />
      </TabPanel>

      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          size="large"
          sx={{ width: isMobile ? '100%' : 'auto' }}
          onClick={() => navigate(buildLangHref('/contents/lecture', lang))}
        >
          목록
        </Button>
      </Stack>
    </Box>
  )
}
